import * as R from 'ramda';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MessagePanel from '../../common/components/MessagePanel/MessagePanel';
import {
  MessagePanelSubDescription,
  MessagePanelSubDescriptionIcon,
} from '../../common/components/MessagePanel/MessagePanelProps';
import { Provider } from '../../config';
import FlowMode from '../../providers/validation/FlowMode';
import { ValidationContext } from '../../providers/ValidationContext';
import { FlowConfigContext } from '../../providers/FlowConfigContext';
import { ValidationStatus } from '../../services/validation';
import withRouteAnalytics from '../../services/analytics/withRouteAnalytics';
import { Screens } from '../../services/analytics/screens';

function ValidationRejectedScreen(): JSX.Element {
  const { initialized, flowFailed } = useContext(FlowConfigContext);
  const { deleteVerification, validationStatus, validationErrors, flowMode } = useContext(ValidationContext);
  const { i18n } = useTranslation();
  const [errorMessages, setErrorMessages] = useState<MessagePanelSubDescription[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [title, setTitle] = useState('screens.documentPass.rejected.title_error');
  const [buttonLabel, setButtonLabel] = useState<string | undefined>('screens.documentPass.rejected.buttonLabel');

  async function performVerificationDeletion() {
    const result = await deleteVerification();
    if (result.isFailed()) {
      result.logError('ValidationRejectedScreen: Error deleting verification');
    }

    setLoading(false);
  }

  useEffect(() => {
    if (!initialized) {
      return;
    }

    performVerificationDeletion();
  }, [initialized]);

  const handleConfirm = () => {
    flowFailed({ provider: Provider.WEB });
  };

  useEffect(() => {
    const subDescriptions: MessagePanelSubDescription[] = [];
    // Default to generic error if no error codes are supplied
    if (R.isNil(validationErrors) || R.isEmpty(validationErrors)) {
      subDescriptions.push({
        title: 'screens.documentPass.rejected.errors.generic',
        icon: MessagePanelSubDescriptionIcon.DOT,
      });
      setErrorMessages(subDescriptions);
    }
    validationErrors.forEach((error) => {
      if (i18n.exists(`screens.documentPass.rejected.errors.${error}`)) {
        // Use generic error if no error mapping is found for the error code
        subDescriptions.push({
          title: `screens.documentPass.rejected.errors.${error}`,
          icon: MessagePanelSubDescriptionIcon.DOT,
        });
      } else {
        subDescriptions.push({
          title: 'screens.documentPass.rejected.errors.generic',
          icon: MessagePanelSubDescriptionIcon.DOT,
        });
      }
    });
    setErrorMessages(R.uniq(subDescriptions));
  }, [validationErrors]);

  useEffect(() => {
    if (flowMode === FlowMode.partial) {
      setButtonLabel(undefined);
    }
  }, [flowMode]);

  useEffect(() => {
    setTitle(`screens.documentPass.rejected.title${validationStatus !== ValidationStatus.failed ? '_error' : ''}`);
  }, [validationStatus]);

  return (
    <MessagePanel
      title={title}
      description="screens.documentPass.rejected.description"
      subDescriptions={errorMessages}
      bottomDescription="screens.documentPass.rejected.bottomDescription"
      dataTestId="REJECTED_SCREEN"
      buttonLabel={buttonLabel}
      loading={loading}
      onConfirm={handleConfirm}
      showUserInfo
      showSupportLink
    />
  );
}
export default withRouteAnalytics(ValidationRejectedScreen, Screens.DOCUMENT_REJECTED);
